<script setup lang="ts">
import { dialogEvents } from "@/data-views/dialogs";

////////////////////////////////////////////////////////////
//////////////////// VARIABLES /////////////////////////////
////////////////////////////////////////////////////////////
const loading = ref(false);
const isVisible = ref(false);
const state = reactive({
  message: "",
  onConfirm: async () => {},
  onCancel: async () => {},
});

////////////////////////////////////////////////////////////
//////////////////// METHODS ///////////////////////////////
////////////////////////////////////////////////////////////
/**
 * Load the dialog
 */
async function load(
  message: string,
  onConfirm: () => Promise<void>,
  onCancel: () => Promise<void>,
): Promise<void> {
  // Start loading
  loading.value = true;

  // Set the state
  state.message = message;
  state.onConfirm = onConfirm;
  state.onCancel = onCancel;

  // Stop loading
  loading.value = false;
}

/**
 * Confirm the dialog
 */
async function confirm(): Promise<void> {
  // Start loading
  loading.value = true;

  // Call the callback
  if (state.onConfirm) await state.onConfirm();

  // Stop loading
  loading.value = false;

  // Close dialog
  isVisible.value = false;
}

/**
 * Cancel the dialog
 */
async function cancel(): Promise<void> {
  // Start loading
  loading.value = true;

  // Call the callback
  if (state.onCancel) await state.onCancel();

  // Stop loading
  loading.value = false;

  // Close dialog
  isVisible.value = false;
}

////////////////////////////////////////////////////////////
//////////////////// LIFECYCLE /////////////////////////////
////////////////////////////////////////////////////////////
onMounted(() => {
  window.addEventListener(
    dialogEvents.ConfirmDialog.OPEN,
    ({ detail }: any) => {
      // Load
      void load(detail.message, detail.onConfirm, detail.onCancel);

      // Set the visibility to visible
      isVisible.value = true;
    },
  );
  window.addEventListener(dialogEvents.ConfirmDialog.CLOSE, () => {
    // Set the visibility to hidden
    isVisible.value = false;
  });
});
</script>

<template>
  <!-- 👉 Confirm Dialog -->
  <VDialog max-width="500" :model-value="isVisible">
    <VCard :loading="loading" class="text-center px-10 py-6">
      <VCardText>
        <h6 class="text-lg font-weight-medium">
          {{ $t(state.message) }}
        </h6>
      </VCardText>

      <VCardText class="d-flex align-center justify-center gap-2">
        <VBtn :disabled="loading" variant="elevated" @click="confirm">
          {{ $t("Confirm") }}
        </VBtn>

        <VBtn
          :disabled="loading"
          color="secondary"
          variant="tonal"
          @click="cancel"
        >
          {{ $t("Cancel") }}
        </VBtn>
      </VCardText>
    </VCard>
  </VDialog>
</template>
